import axios from "axios";
import {
    arrayUnion,
    documentId,
    limit,
    orderBy,
    serverTimestamp,
    Timestamp,
    where,
} from "firebase/firestore";
import papa from "papaparse";
import { useMemo, useRef } from "react";
import { generate } from "short-uuid";
import { z } from "zod";

import {
    getFirebaseAuth,
    getFirebaseFunction,
    getFirebaseStorageDownloadUrl,
} from "../corvusFirebase.js";
import { naturalSort } from "../utils/sortingUtils.js";
import * as firestoreWrappers from "./firestoreWrappers.generated.jsx";
import { STATE_CANCELLED, STATE_PENDING } from "./missionApi";
import * as rtdbWrappers from "./rtdbWrappers.generated.jsx";

export const CORVUS_ORGANIZATION_ID = "xu9pkLgk4tQ1dOnfyqVM";

export function addAppToMission(organizationId, locationId, missionId, appId) {
    return firestoreWrappers
        .MissionDocument(organizationId, locationId, missionId)
        .update({
            apps: arrayUnion({ id: appId, state: STATE_CANCELLED }),
        });
}

export async function updateMissionCloudExports(
    organizationId,
    locationId,
    missionId,
    cloudConnection,
) {
    return firestoreWrappers
        .MissionDocument(organizationId, locationId, missionId)
        .transactionUpdate((missionDoc) => {
            if (!missionDoc) {
                throw new Error(`Mission ${missionId} does not exist`);
            }

            const selectedParts = new Set(
                (cloudConnection ?? []).flatMap(
                    (connection) => connection.parts ?? [],
                ),
            );

            const apps = (missionDoc.apps ?? []).map((app) => ({
                ...app,
                settings: {
                    ...(app.settings ?? {}),
                    cloud_connection: cloudConnection ?? [],
                },
            }));

            const cloudExportStates = {
                ...(missionDoc.cloudExportStates ?? {}),
                ...Object.fromEntries(
                    [...selectedParts].map((part) => [part, STATE_CANCELLED]),
                ),
            };

            const update = {
                apps,
                cloudExportStates,
            };

            if (selectedParts.size > 0) {
                update["states.exporting"] = STATE_CANCELLED;
            }

            return update;
        });
}

export function sendFeedback(userId, feedback) {
    console.log(`Sending feedback ...`);
    firestoreWrappers
        .UserFeedbackDocument(userId)
        .update({
            feedbacks: arrayUnion({
                createdAt: Timestamp.now(),
                description: feedback,
            }),
        })
        .catch((error) => {
            // If the document doesn't exist, create it
            if (error.code === "not-found") {
                firestoreWrappers.UserFeedbackDocument(userId).set({
                    feedbacks: [
                        {
                            createdAt: Timestamp.now(),
                            description: feedback,
                        },
                    ],
                });
            } else {
                console.error("Error sending feedback:", error);
            }
        });
}
export function setAreas(organizationId, locationId, newAreas) {
    console.log(`Setting areas ...`);
    newAreas = newAreas.areas ? newAreas : { areas: newAreas };

    // TODO : Drone can not deserialize this yet, but we need it in the UI
    newAreas.updatedAt = serverTimestamp();

    return firestoreWrappers
        .AreasDocument(organizationId, locationId)
        .set(newAreas);
}

export function setFlyzones(organizationId, locationId, newFlyzones) {
    console.log(`Setting flyzones ...`);
    newFlyzones = newFlyzones.flyzones
        ? newFlyzones
        : { flyzones: newFlyzones };

    // if a flyzone has no z values, use default values
    for (let i = 0; i < newFlyzones.flyzones.length; i++) {
        newFlyzones.flyzones[i].z_min = newFlyzones.flyzones[i].z_min ?? 0;
        newFlyzones.flyzones[i].z_max = newFlyzones.flyzones[i].z_max ?? 4;
    }

    newFlyzones.updatedAt = serverTimestamp();

    return firestoreWrappers
        .FlyzonesDocument(organizationId, locationId)
        .set(newFlyzones);
}

export function setMarkers(organizationId, locationId, newMarkers) {
    console.log(`Setting markers ...`);
    newMarkers = newMarkers.markers ? newMarkers : { markers: newMarkers };

    // TODO : Drone can not deserialize this yet, but we need it in the UI
    newMarkers.updatedAt = serverTimestamp();

    return firestoreWrappers
        .MarkersDocument(organizationId, locationId)
        .set(newMarkers);
}

export const STREAMER_SETTINGS_MAP = {
    hires: "enable_hires_stream",
    payload: "enable_payload_stream",
    qvio: "enable_qvio_stream",
    vio: "enable_qvio_stream",
    left_payload: "enable_left_stream",
    right_payload: "enable_right_stream",
};

export function setLivestreamEnabled(droneId, cameraNames, enabled) {
    const cameras = Array.isArray(cameraNames) ? cameraNames : [cameraNames];

    console.log(
        `${enabled ? "Enabling" : "Disabling"} livestream(s): ${cameras.join(", ")} of ${droneId}`,
    );

    const update = {};

    let hasValidSetting = false;

    for (const cameraName of cameras) {
        if (!cameraName) continue;

        const key = cameraName.toLowerCase();
        const settingName = STREAMER_SETTINGS_MAP[key];

        if (!settingName) {
            console.warn(
                `No streamer setting found for cameraName: ${cameraName}`,
            );
            continue;
        }

        update[`streamer.${settingName}`] = enabled;
        hasValidSetting = true;
    }

    if (!hasValidSetting) {
        return Promise.reject(
            new Error("No valid camera names provided for livestream update"),
        );
    }

    return firestoreWrappers.DroneConfigDocument(droneId).update(update);
}

export function deleteMission(organizationId, locationId, missionId) {
    return firestoreWrappers
        .MissionDocument(organizationId, locationId, missionId)
        .update({ deleted: true });
}

export function restoreMission(organizationId, locationId, missionId) {
    return firestoreWrappers
        .MissionDocument(organizationId, locationId, missionId)
        .update({ deleted: false });
}

export function deleteLocation(organizationId, locationId) {
    return firestoreWrappers
        .LocationDocument(organizationId, locationId)
        .update({ deleted: true });
}

export function restoreLocation(organizationId, locationId) {
    return firestoreWrappers
        .LocationDocument(organizationId, locationId)
        .update({ deleted: false });
}

export function deleteOrganization(organizationId) {
    return firestoreWrappers
        .OrganizationDocument(organizationId)
        .update({ deleted: true });
}

export function restoreOrganization(organizationId) {
    return firestoreWrappers
        .OrganizationDocument(organizationId)
        .update({ deleted: false });
}

export async function clearDroneErrors(droneId) {
    console.log(`Clearing errors for '${droneId}' ...`);

    const callable = getFirebaseFunction("clearDroneErrors", "europe-west2");
    const result = await callable({ droneId });
    return result.data;
}

export function useCorvusApps(options) {
    return firestoreWrappers.useAppCollection(
        [orderBy("name", "asc")],
        options,
    );
}

export function useLocationApps(organizationId, locationId, options = {}) {
    const { mode = "live" } = options; // use mode which is provided in options, defaults to 'live'
    const [location] = firestoreWrappers.useLocationDocument(
        organizationId,
        locationId,
        { mode: mode },
    );
    const locationAppIds = useMemo(
        () => (location?.apps || []).map((app) => app.id),
        [location?.apps],
    );
    return firestoreWrappers.useAppCollection(
        [where(documentId(), "in", locationAppIds), orderBy("name", "asc")],
        options,
    );
}

export function getLocationsForApp(appId, locations) {
    const appLocations = [];

    for (const location of locations ?? []) {
        if (location?.deleted !== true) {
            for (const appRef of location.apps ?? []) {
                if (appRef.id === appId) {
                    appLocations.push(location);
                    break; // app found in this location, no need to keep scanning
                }
            }
        }
    }

    return appLocations;
}

export function useDockDroneIds(organizationId, locationId, dockId) {
    console.log(
        `Getting droneIds with dockId ${dockId} from organization ${organizationId}, location ${locationId}...`,
    );
    const [droneConfigs, isLoading, error] = useDroneConfigsFromLocation(
        organizationId,
        locationId,
    );

    return [
        droneConfigs
            .filter((doc) => doc.docks.some((dock) => dock.id === dockId))
            .map((doc) => doc.id),
        isLoading,
        error,
    ];
}

export function useDroneConfigsFromLocation(
    organizationId,
    locationId,
    options,
) {
    return firestoreWrappers.useDroneConfigCollection(
        [
            where("organizationId", "==", organizationId),
            where("locationId", "==", locationId),
        ],
        options,
    );
}

export function useDroneConfigsFromOrganization(organizationId, options) {
    return firestoreWrappers.useDroneConfigCollection(
        [where("organizationId", "==", organizationId)],
        options,
    );
}

export function useDroneIdsFromLocation(
    organizationId,
    locationId,
    options = {},
) {
    const [droneConfigsMap, isLoading, error] = useDroneConfigsFromLocation(
        organizationId,
        locationId,
        {
            ...options,
            type: "map",
        },
    );

    const previousIdsRef = useRef([]);

    const droneIds = useMemo(() => {
        const newIds = Object.keys(droneConfigsMap ?? {})
            .map((id) => id.toUpperCase())
            .sort();

        const previousIds = previousIdsRef.current;

        const unchanged =
            previousIds.length === newIds.length &&
            previousIds.every((id, index) => id === newIds[index]);

        if (unchanged) {
            return previousIds;
        }

        previousIdsRef.current = newIds;
        return newIds;
    }, [droneConfigsMap]);

    return [droneIds, isLoading, error];
}

export function useDroneStatesFromLocation(
    organizationId,
    locationId,
    options = {},
) {
    const [droneIds, isLoadingIds, idsError] = useDroneIdsFromLocation(
        organizationId,
        locationId,
        options,
    );

    const [droneStates, isLoadingStates, statesError] =
        rtdbWrappers.useDroneStateRtdbDocuments(droneIds, options);

    return [
        droneStates,
        isLoadingIds || isLoadingStates,
        idsError || statesError,
    ];
}

export function useAdminMissions(
    maxMissions = 100,
    showCorvusMissions = false,
    showDeleted = false,
    droneIds = [],
    organizationId,
    startDate,
    endDate,
    options = {},
) {
    const constraints = [
        where("deleted", "==", showDeleted),
        where("isCorvusOrganization", "==", showCorvusMissions),
        ...(organizationId !== undefined
            ? [where("organizationId", "==", organizationId)]
            : []),
        ...(droneIds.length > 0
            ? [where("droneIds", "array-contains-any", droneIds)]
            : []),
        ...(startDate ? [where("timestamp", ">=", startDate)] : []),
        ...(endDate ? [where("timestamp", "<=", endDate)] : []),
        orderBy("timestamp", "desc"),
        limit(maxMissions),
    ];

    return firestoreWrappers.useMissionCollectionGroup(constraints, options);
}

export function useAppErrorMissions(
    maxMissions = 100,
    showCorvusMissions = false,
    showDeleted = false,
    droneIds = [],
    startDate,
    endDate,
    options = {},
) {
    const constraints = [
        where("deleted", "==", showDeleted),
        where("appError", "==", true),
        where("isCorvusOrganization", "==", showCorvusMissions),
        ...(droneIds.length > 0
            ? [where("droneIds", "array-contains-any", droneIds)]
            : []),
        ...(startDate ? [where("timestamp", ">=", startDate)] : []),
        ...(endDate ? [where("timestamp", "<=", endDate)] : []),
        orderBy("timestamp", "desc"),
        limit(maxMissions),
    ];

    return firestoreWrappers.useMissionCollectionGroup(constraints, options);
}

export function removeAppError(organizationId, locationId, missionId) {
    firestoreWrappers
        .MissionDocument(organizationId, locationId, missionId)
        .update({ appError: false });
}

export function addAppError(organizationId, locationId, missionId) {
    firestoreWrappers
        .MissionDocument(organizationId, locationId, missionId)
        .update({ appError: true });
}

export async function checkMissionIdExists(
    organizationId,
    locationId,
    missionId,
) {
    return firestoreWrappers
        .MissionDocument(organizationId, locationId, missionId)
        .get()
        .then((missionDoc) => {
            return !(missionDoc.deleted === true); // Also return as existing if deleted field does not exist
        })
        .catch(() => {
            // non-existing document also lands here
            return false;
        });
}

export function useMissionsFromLocation(
    organizationId,
    locationId,
    maxMissions = 50,
    showDeleted = false,
    droneIds = [],
    startDate,
    endDate,
) {
    const constraints = [
        where("deleted", "==", showDeleted),
        where(
            "isCorvusOrganization",
            "==",
            organizationId === CORVUS_ORGANIZATION_ID,
        ), // Adding this so we can reuse the same query as the admin missions page
        ...(droneIds.length > 0
            ? [where("droneIds", "array-contains-any", droneIds)]
            : []),
        ...(startDate ? [where("timestamp", ">=", startDate)] : []),
        ...(endDate ? [where("timestamp", "<=", endDate)] : []),
        orderBy("timestamp", "desc"),
        limit(maxMissions),
    ];

    return firestoreWrappers.useMissionCollection(
        organizationId,
        locationId,
        constraints,
    );
}

export function useDroneMissions(
    organizationId,
    locationId,
    droneId,
    missionLimit,
    options,
) {
    return firestoreWrappers.useMissionCollection(
        organizationId,
        locationId,
        [
            where("deleted", "==", false),
            where("drone.id", "==", droneId ?? "NoMatch"),
            orderBy("timestamp", "desc"),
            limit(missionLimit),
        ],
        options,
    );
}

function sendDroneCommand(droneId, command) {
    console.log(`Sending '${command}' command ...`);
    return firestoreWrappers.DroneCommandDocument(droneId).update({
        commands: arrayUnion({
            id: generate(),
            type: command,
        }),
    });
}

export function sendStartUpload(droneId) {
    return sendDroneCommand(droneId, "START_UPLOAD");
}

export function sendShutdown(droneId) {
    return sendDroneCommand(droneId, "SHUTDOWN");
}

export function sendReboot(droneId) {
    return sendDroneCommand(droneId, "REBOOT");
}

export function sendKill(droneId, lastMissions) {
    const executeCommand = sendDroneCommand(droneId, "KILL");

    // store the event in the last missions
    lastMissions = lastMissions || [];
    lastMissions.forEach((mission) => {
        storeEvent(
            mission.organizationId,
            mission.locationId,
            mission.missionId,
            "DRONE_KILLED",
            { drone: { id: droneId } },
        );
    });

    return executeCommand;
}

export function sendEmergencyLand(droneId, lastMissions) {
    console.log(droneId, lastMissions);
    const executeCommand = sendDroneCommand(droneId, "EMERGENCY_LAND");

    // store the event in the last missions
    lastMissions = lastMissions || [];
    lastMissions.forEach((mission) => {
        storeEvent(
            mission.organizationId,
            mission.locationId,
            mission.missionId,
            "EMERGENCY_LANDING",
            { drone: { id: droneId } },
        );
    });

    return executeCommand;
}

export function sendAbort(droneId, lastMissions) {
    const executeCommand = sendDroneCommand(droneId, "ABORT_FLIGHT");

    // store the event in the last missions
    lastMissions = lastMissions || [];
    lastMissions.forEach((mission) => {
        storeAbortMissionEvent(
            mission.organizationId,
            mission.locationId,
            mission.missionId,
        );
        cleanUpMissionHandler(
            mission.organizationId,
            mission.locationId,
            mission.missionId,
        );
    });

    return executeCommand;
}

export function sendPause(droneId) {
    return sendDroneCommand(droneId, "PAUSE_FLIGHT");
}

export function sendResume(droneId) {
    return sendDroneCommand(droneId, "RESUME_FLIGHT");
}

export function sendGoToSleep(droneId) {
    return sendDroneCommand(droneId, "GO_TO_SLEEP");
}

export function sendWakeUp(droneId) {
    return sendDroneCommand(droneId, "WAKE_UP");
}

const timezoneCache = new Map();

export async function getTimezone(organizationId, locationId) {
    const key = `${organizationId}_${locationId}`;

    if (timezoneCache.has(key)) {
        return timezoneCache.get(key);
    }

    const location = await firestoreWrappers
        .LocationDocument(organizationId, locationId)
        .get();

    const timezone = location?.timezone || "UTC";

    timezoneCache.set(key, timezone);

    return timezone;
}

export function droneHasActiveMissions(droneState) {
    return (droneState?.missions || []).length > 0;
}

export function droneIsFlying(widgets) {
    const isFlyingState = (state) => {
        return [
            "Prepare",
            "Arming",
            "Take off",
            "Calibrating",
            "Flying",
            "Returning",
            "Returning to home",
            "Paused",
            "Landing",
        ].includes(state);
    };

    if (widgets?.currentFlightId?.data !== undefined)
        return widgets.currentFlightId.data !== "";
    if (widgets?.status?.data !== undefined)
        return isFlyingState(widgets.status.data);
    return false;
}

export function droneIsPaused(widgets) {
    return widgets?.isPaused?.data; // Returns undefined when isPaused is not yet there
}

export function toFolderDownloadItem(folderPath) {
    return { type: "folder", path: folderPath };
}

export function toFileDownloadItem(filePath) {
    return { type: "file", path: filePath };
}

export function toMeasurementQueryDownloadItem({
    organizationId,
    locationId,
    missionId,
    flightId,
    measurementId,
}) {
    return {
        type: "measurementQuery",
        organizationId,
        locationId,
        missionId,
        flightId,
        measurementId,
    };
}

export async function canDownload(downloadItems) {
    try {
        const callable = getFirebaseFunction("canDownload", "europe-west2");

        const result = await callable({ items: downloadItems });

        return !!result?.data?.ok;
    } catch (err) {
        console.log("canDownload failed:", err);
        return false;
    }
}

export async function startDownload(downloadItems, filename) {
    const callable = getFirebaseFunction("prepareDownload", "europe-west2");

    const result = await callable({ items: downloadItems, filename });

    if (!result?.data?.jobId || !result?.data?.downloadToken) {
        throw new Error("Invalid download response");
    }

    return result?.data;
}

function triggerDownload(jobId, downloadToken) {
    const url = `https://download-api-493740961644.europe-west2.run.app/v1/zip/download/${jobId}?token=${downloadToken}`;

    const link = document.createElement("a");
    link.href = url;
    link.download = "";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

export async function handleDownload(downloadItems, filename = "files.zip") {
    const { jobId, downloadToken } = await startDownload(
        downloadItems,
        filename,
    );
    triggerDownload(jobId, downloadToken);
}

export async function getFlightPreview(
    organizationId,
    locationId,
    droneId,
    taskId,
    routePlannerVersion,
    startPosition,
) {
    routePlannerVersion = routePlannerVersion || "V2";
    console.log(`Getting flight preview ${routePlannerVersion} ...`);

    const url = await getRouteplannerUrl(routePlannerVersion);
    if (!url) {
        throw Error(`Unknown routePlanner version ${routePlannerVersion}`);
    }

    const jsonPayload = {
        task: { id: taskId },
        organization: { id: organizationId },
        location: { id: locationId },
        preview: true,
        ...(droneId ? { drone: { id: droneId } } : {}),
        ...(startPosition ? { startPosition } : {}),
    };
    const user = getFirebaseAuth().currentUser;
    const token = await user.getIdToken();
    const config = {
        headers: { authorization: `Bearer ${token}` },
    };

    // Let caller handle the exceptions
    return axios.post(url, jsonPayload, config);
}

export async function getMissionResumePreview(
    organizationId,
    locationId,
    missionId,
    droneId,
    routePlannerVersion,
) {
    routePlannerVersion = routePlannerVersion || "V2";
    console.log(`Getting mission resume preview ${routePlannerVersion} ...`);

    const url = await getRouteplannerUrl(routePlannerVersion);
    if (!url) {
        throw Error(`Unknown routePlanner version ${routePlannerVersion}`);
    }

    const jsonPayload = {
        drone: { id: droneId },
        mission: { id: missionId },
        organization: { id: organizationId },
        location: { id: locationId },
        preview: true,
    };
    const user = getFirebaseAuth().currentUser;
    const token = await user.getIdToken();
    const config = {
        headers: { authorization: `Bearer ${token}` },
    };

    // Let caller handle the exceptions
    return axios.post(url, jsonPayload, config);
}

const FLYZONES_COLUMNS = {
    name: "str",
    x_min: "float",
    y_min: "float",
    x_max: "float",
    y_max: "float",
    z_min: "float",
    z_max: "float",
    yaw: "float",
    speed: "float",
    flyable: "bool",
};
const MARKERS_COLUMNS = [
    "id",
    "valid",
    "x",
    "y",
    "z",
    "yaw",
    "width",
    "height",
    "uncertainty",
];

export function validateCsvColumns(csv, expectedColumns, exact = true) {
    const options = {
        header: true,
        skipEmptyLines: true,
    };
    const parsedData = papa.parse(csv, options);
    const csvColumns = parsedData.meta.fields;

    const missingColumns = expectedColumns.filter(
        (col) => !csvColumns.includes(col),
    );
    const extraColumns = csvColumns.filter(
        (col) => !expectedColumns.includes(col),
    );

    if (missingColumns.length > 0 || (exact && extraColumns.length > 0)) {
        let errorMessage = "Invalid CSV format.";

        if (missingColumns.length > 0) {
            errorMessage += ` Missing columns: ${missingColumns.join(", ")}.`;
        }

        if (exact && extraColumns.length > 0) {
            errorMessage += ` Extra columns: ${extraColumns.join(", ")}.`;
        }

        throw new Error(errorMessage);
    }
}

export function setFlyzonesFromCsv(organizationId, locationId, flyzonesCsv) {
    validateCsvColumns(flyzonesCsv, Object.keys(FLYZONES_COLUMNS));

    const options = {
        columns: Object.keys(FLYZONES_COLUMNS),
        header: true,
        skipEmptyLines: true,
    };
    const { data: flyzones } = papa.parse(flyzonesCsv, options);

    const rowErrors = {};
    const typedFlyzones = [];

    flyzones.forEach((row, index) => {
        const rowNumber = index + 2; // Account for CSV header
        const typedRow = {};
        const errors = [];

        for (const key of Object.keys(FLYZONES_COLUMNS)) {
            const raw = row[key];

            if (raw === undefined || raw === null) {
                errors.push(`Missing value for "${key}"`);
                continue;
            }

            const value = raw.trim();
            if (value === "") {
                errors.push(`Empty value for "${key}"`);
                continue;
            }

            switch (FLYZONES_COLUMNS[key]) {
                case "bool": {
                    const lower = value.toLowerCase();
                    if (!["true", "false"].includes(lower)) {
                        console.log(`'${key}': '${value}', '${lower}'`);
                        errors.push(`Invalid boolean "${value}" for "${key}"`);
                    } else {
                        typedRow[key] = lower === "true";
                    }
                    break;
                }
                case "int": {
                    const parsed = parseInt(value, 10);
                    if (Number.isNaN(parsed)) {
                        errors.push(`Invalid integer "${value}" for "${key}"`);
                    } else {
                        typedRow[key] = parsed;
                    }
                    break;
                }
                case "float": {
                    const parsed = parseFloat(value);
                    if (Number.isNaN(parsed)) {
                        errors.push(`Invalid float "${value}" for "${key}"`);
                    } else {
                        typedRow[key] = parsed;
                    }
                    break;
                }
                default:
                    typedRow[key] = value;
            }
        }

        if (errors.length === 0) {
            if (typedRow.x_min > typedRow.x_max) {
                errors.push(
                    `x_min (${typedRow.x_min}) is greater than x_max (${typedRow.x_max})`,
                );
            }
            if (typedRow.y_min > typedRow.y_max) {
                errors.push(
                    `y_min (${typedRow.y_min}) is greater than y_max (${typedRow.y_max})`,
                );
            }
            if (typedRow.z_min > typedRow.z_max) {
                errors.push(
                    `z_min (${typedRow.z_min}) is greater than z_max (${typedRow.z_max})`,
                );
            }
            if (typedRow.speed <= 0) {
                errors.push(`speed (${typedRow.speed}) must be greater than 0`);
            }
        }

        if (errors.length > 0) {
            rowErrors[rowNumber] = errors;
        } else {
            typedFlyzones.push(typedRow);
        }
    });

    if (Object.keys(rowErrors).length > 0) {
        const formatted = Object.entries(rowErrors)
            .map(([row, errs]) => `Row ${row}:\n  - ${errs.join("\n  - ")}`)
            .join("\n\n");
        throw new Error(`Invalid CSV:\n\n${formatted}`);
    }

    return setFlyzones(organizationId, locationId, typedFlyzones);
}

export function setMarkersFromCsv(organizationId, locationId, markersCsv) {
    validateCsvColumns(markersCsv, MARKERS_COLUMNS);

    const options = {
        columns: MARKERS_COLUMNS,
        header: true,
        skipEmptyLines: true,
    };
    const { data: markers } = papa.parse(markersCsv, options);

    const requiredFields = MARKERS_COLUMNS.filter(
        (col) => col !== "uncertainty",
    );
    const idCounts = {};
    const rowErrors = {};
    const validMarkers = [];

    markers.forEach((row, index) => {
        const rowNumber = index + 2;
        const errors = [];

        // Trim all values first
        Object.keys(row).forEach((key) => {
            if (typeof row[key] === "string") {
                row[key] = row[key].trim();
            }
        });

        // 1. Required field check (non-empty)
        for (const key of requiredFields) {
            const value = row[key];
            if (value === undefined || value === "") {
                errors.push(`Missing value for "${key}"`);
            }
        }

        // 2. Duplicate ID check
        const id = row.id;
        if (id) {
            if (idCounts[id]) {
                idCounts[id]++;
            } else {
                idCounts[id] = 1;
            }
        }

        if (errors.length > 0) {
            rowErrors[rowNumber] = errors;
        } else {
            validMarkers.push(row);
        }
    });

    // 3. Collect duplicate ID errors
    const duplicateIds = Object.entries(idCounts)
        .filter(([, count]) => count > 1)
        .map(([id]) => id);

    if (duplicateIds.length > 0) {
        duplicateIds.forEach((id) => {
            // Find all rows that used this duplicate id
            markers.forEach((row, index) => {
                if (row.id === id) {
                    const rowNumber = index + 2;
                    if (!rowErrors[rowNumber]) rowErrors[rowNumber] = [];
                    rowErrors[rowNumber].push(`Duplicate ID "${id}"`);
                }
            });
        });
    }

    // 4. Throw errors if any
    if (Object.keys(rowErrors).length > 0) {
        const formatted = Object.entries(rowErrors)
            .map(([row, errs]) => `Row ${row}:\n  - ${errs.join("\n  - ")}`)
            .join("\n\n");
        throw new Error(`Invalid Marker CSV:\n\n${formatted}`);
    }

    return setMarkers(organizationId, locationId, validMarkers);
}

const coordinateSchema = z.object({
    x: z.number(),
    y: z.number(),
});

const singleAreaSchema = z
    .object({
        min: coordinateSchema,
        max: coordinateSchema,
        z: z.number().optional().default(0),
    })
    .superRefine((data, ctx) => {
        if (data.min.x > data.max.x) {
            ctx.addIssue({
                path: ["min", "x"],
                code: z.ZodIssueCode.custom,
                message: `min.x (${data.min.x}) must be ≤ max.x (${data.max.x})`,
            });
        }
        if (data.min.y > data.max.y) {
            ctx.addIssue({
                path: ["min", "y"],
                code: z.ZodIssueCode.custom,
                message: `min.y (${data.min.y}) must be ≤ max.y (${data.max.y})`,
            });
        }
    });

const areaSchema = z.record(z.string(), singleAreaSchema); // area coordinate indexed by pole name

const areasSchema = z.object({
    areas: z.record(z.string(), areaSchema), // area first indexed by bay name
});

export function setAreasFromJson(organizationId, locationId, areasJson) {
    try {
        const parsed = areasSchema.parse(areasJson);
        return setAreas(organizationId, locationId, parsed);
    } catch (err) {
        if (err instanceof z.ZodError) {
            const message = err.errors
                .map((e) => `- ${e.path.join(".")}: ${e.message}`)
                .join("\n");
            throw new Error(`Invalid Areas JSON:\n\n${message}`);
        } else {
            throw err;
        }
    }
}

export async function getBatch(organizationId, locationId, batchId) {
    console.log("download batches", organizationId, locationId, batchId);
    let unsortedBatches = {};
    let date = null;
    try {
        const doc = await firestoreWrappers
            .BatchesDocument(organizationId, locationId, batchId)
            .get();
        unsortedBatches = doc.batches ?? {};
        date = doc.created;
    } catch (e) {
        console.error("Error fetching areas:", e);
        unsortedBatches = {}; // fallback
        date = null;
    }

    const sortedBatches = Object.keys(unsortedBatches)
        .sort(naturalSort)
        .reduce((acc, batchName) => {
            const blocks = unsortedBatches[batchName];

            const sortedBlocks = Object.keys(blocks)
                .sort(naturalSort)
                .reduce((blockAcc, blockKey) => {
                    const block = blocks[blockKey];

                    blockAcc[blockKey] = {
                        min: {
                            x: block.min.x,
                            y: block.min.y,
                        },
                        max: {
                            x: block.max.x,
                            y: block.max.y,
                        },
                        ...(block.z !== undefined ? { z: block.z } : {}),
                    };

                    return blockAcc;
                }, {});

            acc[batchName] = sortedBlocks;
            return acc;
        }, {});

    return { batches: sortedBatches, created: date };
}

export async function deleteBatch(organizationId, locationId, batchId) {
    const doc_ref = firestoreWrappers.BatchesDocument(
        organizationId,
        locationId,
        batchId,
    );
    await doc_ref.delete();
}

function persistedBatchData(batch) {
    if (
        !(batch?.created instanceof Date) ||
        Number.isNaN(batch.created.getTime())
    ) {
        throw new Error("A valid batch created date is required");
    }

    return {
        batches: batch.batches || [],
        created: batch.created,
    };
}

export async function updateBatch(organizationId, locationId, batchId, batch) {
    if (!batchId) {
        throw new Error("A batch ID is required to update a batch");
    }

    const doc = firestoreWrappers.BatchesDocument(
        organizationId,
        locationId,
        batchId,
    );
    await doc.update(persistedBatchData(batch));
}

export async function createBatch(organizationId, locationId, batch) {
    const batchId = await firestoreWrappers
        .BatchesCollection(organizationId, locationId)
        .add({
            ...persistedBatchData(batch),
        });
    return batchId;
}

export async function getAreasAsJson(organizationId, locationId) {
    let unsortedAreas = {};
    try {
        const doc = await firestoreWrappers
            .AreasDocument(organizationId, locationId)
            .get();
        unsortedAreas = doc.areas ?? {};
    } catch (e) {
        console.error("Error fetching areas:", e);
        unsortedAreas = {}; // fallback
    }

    const sortedAreas = Object.keys(unsortedAreas)
        .sort(naturalSort)
        .reduce((acc, areaName) => {
            const blocks = unsortedAreas[areaName];

            const sortedBlocks = Object.keys(blocks)
                .sort(naturalSort)
                .reduce((blockAcc, blockKey) => {
                    const block = blocks[blockKey];

                    blockAcc[blockKey] = {
                        min: {
                            x: block.min.x,
                            y: block.min.y,
                        },
                        max: {
                            x: block.max.x,
                            y: block.max.y,
                        },
                        ...(block.z !== undefined ? { z: block.z } : {}),
                    };

                    return blockAcc;
                }, {});

            acc[areaName] = sortedBlocks;
            return acc;
        }, {});

    return { areas: sortedAreas };
}

export async function getMarkersAsCsv(organizationId, locationId) {
    let markers = [];
    try {
        const doc = await firestoreWrappers
            .MarkersDocument(organizationId, locationId)
            .get();
        markers = doc.markers ?? [];
    } catch (e) {
        console.error("Error fetching markers:", e);
        markers = []; // fallback
    }

    let markersCsv = papa.unparse(markers, {
        columns: MARKERS_COLUMNS,
        header: false,
    });
    markersCsv = `${MARKERS_COLUMNS.join(",")}\n${markersCsv}`;
    return markersCsv;
}

export async function getFlyzonesAsCsv(organizationId, locationId) {
    let flyzones = [];
    try {
        const doc = await firestoreWrappers
            .FlyzonesDocument(organizationId, locationId)
            .get();
        flyzones = (doc.flyzones ?? []).map((flyzone) => ({
            ...flyzone,
            speed: flyzone.speed ?? 5.0, // default value
        }));
    } catch (e) {
        console.error("Error fetching flyzones:", e);
        flyzones = []; // fallback
    }

    // Header does not work for empty flyzones so we do it ourselves
    let flyzonesCsv = papa.unparse(flyzones, {
        columns: Object.keys(FLYZONES_COLUMNS),
        header: false,
    });
    flyzonesCsv = `${Object.keys(FLYZONES_COLUMNS).join(",")}\n${flyzonesCsv}`;
    return flyzonesCsv;
}

export async function getRouteplannerVersions() {
    const routeplanners = firestoreWrappers
        .RouteplannerCollection()
        .list()
        .then((list) => {
            return list.map((routeplaner) => routeplaner.id).sort();
        });
    return routeplanners;
}

export async function getRouteplannerUrl(version) {
    const url = firestoreWrappers
        .RouteplannerDocument(version)
        .get()
        .then((routeplanner) => routeplanner?.url);
    return url;
}

export function downloadResult(
    organizationId,
    locationId,
    missionId,
    resultId,
    fileName,
    openNewTab = true,
) {
    const resultDocPath = firestoreWrappers.ResultDocument(
        organizationId,
        locationId,
        missionId,
        resultId,
    ).path;
    const resultPath = `${resultDocPath}/${fileName}`;

    return getFirebaseStorageDownloadUrl(resultPath).then(async (url) => {
        // console.log("download url: ", url);
        const link = document.createElement("a");
        link.href = url;
        link.target = openNewTab ? "_blank" : "_self"; // Open in a new tab or window
        link.download = fileName; // Set the download attribute with the desired file name
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    });
}

export async function setResultPublished(
    organizationId,
    locationId,
    missionId,
    resultId,
    fileName,
    published,
) {
    await firestoreWrappers
        .ResultDocument(organizationId, locationId, missionId, resultId)
        .transactionUpdate((data) => {
            return {
                files: data.files.map((file) => {
                    if (file.name !== fileName) {
                        return file;
                    } else {
                        file.published = published;
                        return file;
                    }
                }),
            };
        });
}

function storeEvent(
    organizationId,
    locationId,
    missionId,
    eventType,
    eventData,
) {
    const missionDoc = firestoreWrappers.MissionDocument(
        organizationId,
        locationId,
        missionId,
    );
    missionDoc
        .update({
            events: arrayUnion({
                ...eventData,
                type: eventType,
                timestamp: new Date(),
            }),
        })
        .catch((error) => {
            console.error(
                `Error while storing event '${eventType}' in Firestore document '${missionDoc.path}'`,
            );
            console.error(error);
        });
}

export function storeResumeEvent(organizationId, locationId, missionId) {
    storeEvent(organizationId, locationId, missionId, "RESUME_MISSION", {});
}

export function storeExportEvent(organizationId, locationId, missionId) {
    storeEvent(organizationId, locationId, missionId, "EXPORT_MISSION", {});
}

export function storeAbortMissionEvent(organizationId, locationId, missionId) {
    storeEvent(organizationId, locationId, missionId, "MISSION_ABORTED", {});
}

export async function getAppSettings(
    organizationId,
    locationId,
    taskId,
    appId,
    setAppSettings,
) {
    const [appDoc, locDoc, taskDoc] = await Promise.all([
        firestoreWrappers.AppDocument(appId).get(),
        firestoreWrappers.LocationDocument(organizationId, locationId).get(),
        firestoreWrappers
            .TaskDocument(organizationId, locationId, taskId)
            .get(),
    ]);

    // Default settings <- {} if missing
    const appSettings = appDoc?.settings ?? {};

    // Look up this app’s entry in the location’s apps array
    const locApps = locDoc?.apps ?? [];
    const locationAppSettings =
        locApps.find((a) => a.id === appId)?.settings ?? {};

    // Look up this app’s entry in the task’s apps array
    const taskApps = taskDoc?.apps ?? [];
    const taskAppSettings =
        taskApps.find((a) => a.id === appId)?.settings ?? {};

    setAppSettings({
        app: appSettings,
        location: locationAppSettings,
        task: taskAppSettings,
    });
}

export async function updateAppSettings(
    organizationId,
    locationId,
    taskId,
    appId,
    appSettings,
) {
    await firestoreWrappers
        .TaskDocument(organizationId, locationId, taskId)
        .transactionUpdate((taskDoc) => {
            // `taskDoc` is the current document data in the transaction
            const apps = Array.isArray(taskDoc.apps) ? [...taskDoc.apps] : [];

            // find the index of the app we want to update
            const idx = apps.findIndex((a) => a.id === appId);
            if (idx < 0) {
                console.warn(
                    `App ${appId} not found on task ${taskId}, skipping update.`,
                );
                // returning an empty object will result in no writes
                return {};
            }

            // merge in the new settings
            apps[idx] = {
                ...apps[idx],
                settings: appSettings,
            };

            // Return the updated settings so that it is updated
            return { apps };
        });
}

export async function setAppPending(
    organizationId,
    locationId,
    missionId,
    appId,
) {
    return firestoreWrappers
        .MissionDocument(organizationId, locationId, missionId)
        .transactionUpdate((missionDoc) => {
            if (!missionDoc) {
                throw new Error(`Mission ${missionId} does not exist`);
            }

            const apps = missionDoc?.apps || [];
            const updatedApps = apps.map((a) =>
                a.id === appId ? { ...a, state: STATE_PENDING } : a,
            );
            return { apps: updatedApps };
        });
}

export async function setCloudExportPending(
    organizationId,
    locationId,
    missionId,
    exportPart,
) {
    return firestoreWrappers
        .MissionDocument(organizationId, locationId, missionId)
        .transactionUpdate((missionDoc) => {
            if (!missionDoc) {
                throw new Error(`Mission ${missionId} does not exist`);
            }

            return {
                cloudExportStates: {
                    ...(missionDoc.cloudExportStates ?? {}),
                    [exportPart]: STATE_PENDING,
                },
            };
        });
}

function getMissionHandlerUrl(organizationId, locationId) {
    return firestoreWrappers
        .LocationDocument(organizationId, locationId)
        .get()
        .then((locationDoc) => {
            const missionHandlerVersion =
                locationDoc?.missionHandler?.version || "";
            console.log("missionHandlerVersion", missionHandlerVersion);
            if (missionHandlerVersion) {
                return `https://europe-west1-corvus-backend.cloudfunctions.net/missionhandler-${missionHandlerVersion}`;
            } else {
                return "https://europe-west1-corvus-backend.cloudfunctions.net/missionhandler-production";
            }
        });
}

export async function startMissionHandler(
    organizationId,
    locationId,
    taskId,
    droneId,
) {
    console.log("starting mission handler");
    // state the mission handler
    const user = getFirebaseAuth().currentUser;
    const token = await user.getIdToken();

    const url = await getMissionHandlerUrl(organizationId, locationId);
    console.log(url);
    const jsonPayload = {
        mission: {
            organization: { id: organizationId },
            location: { id: locationId },
            task: { id: taskId },
            drones: [{ id: droneId }],
            restart: false,
            onlyApps: false,
        },
    };

    const config = {
        headers: { authorization: `Bearer ${token}` },
    };

    axios.post(url, jsonPayload, config).catch((error) => {
        console.log(error);
    });
}

export async function cleanUpMissionHandler(
    organizationId,
    locationId,
    missionId,
) {
    console.log("starting mission handler clean up", missionId);
    // state the mission handler
    const user = getFirebaseAuth().currentUser;
    const token = await user.getIdToken();

    const url = await getMissionHandlerUrl(organizationId, locationId);
    console.log(url);
    const jsonPayload = {
        mission: {
            cleanUp: true,
            organization: { id: organizationId },
            location: { id: locationId },
            mission: { id: missionId },
            delay: 600,
        },
    };

    const config = {
        headers: { authorization: `Bearer ${token}` },
    };

    axios.post(url, jsonPayload, config).catch((error) => {
        console.log(error);
    });
}

export async function restartMissionHandler(
    organizationId,
    locationId,
    missionId,
    { restart = false, onlyApps = false, onlyCloudExport = false } = {},
) {
    const missionDoc = await firestoreWrappers
        .MissionDocument(organizationId, locationId, missionId)
        .get();

    if (missionDoc.running) {
        return;
    }

    console.log("starting mission handler");

    try {
        const token = await getFirebaseAuth().currentUser.getIdToken();
        const url = await getMissionHandlerUrl(organizationId, locationId);

        await axios.post(
            url,
            {
                mission: {
                    organization: { id: organizationId },
                    location: { id: locationId },
                    mission: { id: missionId },
                    restart,
                    onlyApps,
                    onlyCloudExport,
                },
            },
            {
                headers: {
                    authorization: `Bearer ${token}`,
                },
            },
        );
    } catch (error) {
        console.error(error);
    }
}

export async function notifyUsingMissionHandler(
    organizationId,
    locationId,
    missionId,
) {
    console.log("starting mission handler");
    // state the mission handler
    const user = getFirebaseAuth().currentUser;
    const token = await user.getIdToken();

    const url = await getMissionHandlerUrl(organizationId, locationId);
    console.log(url);
    const jsonPayload = {
        mission: {
            organization: { id: organizationId },
            location: { id: locationId },
            mission: { id: missionId },
            notify: true,
            onlyApps: true,
        },
    };

    const config = {
        headers: { authorization: `Bearer ${token}` },
    };

    axios.post(url, jsonPayload, config).catch((error) => {
        console.log(error);
    });
}

export async function answerUserQuestion(
    organizationId,
    locationId,
    missionId,
    questionId,
    answer,
    user,
) {
    return firestoreWrappers
        .MissionDocument(organizationId, locationId, missionId)
        .transactionUpdate((missionDoc) => {
            if (!missionDoc) {
                throw new Error(`Mission ${missionId} not found`);
            }

            const userQuestions = missionDoc?.userQuestions || [];

            const updatedQuestions = userQuestions.map((q) =>
                q.id === questionId ? { ...q, answer, answerer: user } : q,
            );

            // If nothing changed, throw:
            if (!updatedQuestions.some((q) => q.id === questionId)) {
                throw new Error(`Question ${questionId} not found`);
            }

            return { userQuestions: updatedQuestions };
        })
        .catch((error) => {
            console.error("Error updating answer: ", error);
            throw error;
        });
}

export async function startNewAnalyticsJob(
    organizationId,
    locationId,
    flightId,
) {
    try {
        console.log(
            `Starting new analytics job for ${organizationId}/${locationId}/${flightId}`,
        );
        const callable = getFirebaseFunction(
            "startNewAnalyticsJob",
            "europe-west2",
        );

        const result = await callable({
            organizationId,
            locationId,
            flightId,
        });

        return result?.status === "ok";
    } catch (err) {
        console.log("startNewAnalyticsJob failed:", err);
        return false;
    }
}

export async function getAllUsers() {
    try {
        const result = await getFirebaseFunction(
            "getAllUsers",
            "europe-west2",
        )();
        if (!result?.data) return {};
        return result.data;
    } catch (err) {
        console.log("getAllUsers failed:", err);
        return {};
    }
}
export async function getUserDetails(uidOrList) {
    const uids = Array.isArray(uidOrList) ? uidOrList : [uidOrList];
    const getUserFn = getFirebaseFunction("getUsers", "europe-west2");
    try {
        const result = await getUserFn({ uids });
        if (!result?.data) return {};
        return result.data;
    } catch (err) {
        console.log("getUserDetails failed:", err);
        return {};
    }
}

export async function getSingleUserDetails(uid) {
    const map = await getUserDetails([uid]);
    return map[uid] ?? null;
}

export async function setUserDisabled(uid, disabled) {
    if (typeof disabled !== "boolean") {
        throw new Error("disabled must be a boolean");
    }

    const setUserDisabledFn = getFirebaseFunction(
        "setUserDisabled",
        "europe-west2",
    );
    const res = await setUserDisabledFn({ uid, disabled });
    return res.data;
}

export async function updateUser({
    uid,
    displayName,
    isAdmin,
    organizationId,
}) {
    if (!uid) {
        throw new Error("uid is required");
    }

    if (typeof uid !== "string") {
        throw new Error("uid must be a string");
    }

    const payload = { uid };

    if (typeof displayName === "string") {
        payload.displayName = displayName;
    }

    if (typeof isAdmin === "boolean") {
        payload.isAdmin = isAdmin;
    }

    if (typeof organizationId === "string" && organizationId.trim() !== "") {
        payload.organizationId = organizationId;
    }

    const updateUserFn = getFirebaseFunction("updateUser", "europe-west2");
    const res = await updateUserFn(payload);
    return res.data;
}

export async function createUser({
    email,
    displayName,
    isAdmin,
    organizationId,
    disabled,
}) {
    if (typeof email !== "string") {
        throw new Error("email is required");
    }

    if (typeof isAdmin !== "boolean") {
        throw new Error("isAdmin must be boolean");
    }

    if (typeof organizationId !== "string" || organizationId.trim() === "") {
        throw new Error("organizationId must be a non-empty string");
    }

    const payload = {
        email,
        isAdmin,
        organizationId,
    };

    if (typeof displayName === "string") {
        payload.displayName = displayName;
    }

    if (typeof disabled === "boolean") {
        payload.disabled = disabled;
    }

    const createUserFn = getFirebaseFunction("createUser", "europe-west2");
    const res = await createUserFn(payload);
    return res.data;
}

export async function addCloudConnection(
    organizationId,
    bucketPath,
    credentials,
) {
    const res = await fetch(
        "https://europe-west2-corvus-backend.cloudfunctions.net/createCloudConnection",
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                organizationId,
                bucketPath,
                credentials,
            }),
        },
    );

    const data = await res.json();
    if (!res.ok) throw new Error();

    return data.connectionId;
}

export async function deleteCloudConnection(organizationId, connectionId) {
    await fetch(
        `https://europe-west2-corvus-backend.cloudfunctions.net/deleteCloudConnection/${organizationId}/${connectionId}`,
        {
            method: "DELETE",
        },
    );
}

export async function requestDeleteMissionMeasurements(
    organizationId,
    locationId,
    missionId,
) {
    const requestDeleteFunction = getFirebaseFunction(
        "requestDeleteMissionMeasurements",
        "europe-west2",
    );
    const payload = {
        organizationId,
        locationId,
        missionId,
    };
    const res = await requestDeleteFunction(payload);
    return res.data;
}

export async function requestDeleteFlightTelemetry(
    organizationId,
    locationId,
    flightId,
) {
    const requestDeleteFunction = getFirebaseFunction(
        "requestDeleteFlightTelemetry",
        "europe-west2",
    );
    const payload = {
        organizationId,
        locationId,
        flightId,
    };
    const res = await requestDeleteFunction(payload);
    return res.data;
}
